import breakpoints from 'styles/breakpoints.module.scss';

// Types copied from redux-responsive
type BreakPointsDefaultNames = 'mobile' | 'tablet' | 'desktop' | 'desktopLarge';

type IBreakPoints<BPNames extends string = BreakPointsDefaultNames> = {
  [k in BPNames]: number | string;
};

type IBreakPointResults<BP = IBreakPoints> = { [k in keyof BP]: boolean };

interface IBrowser<BP = IBreakPoints> {
  lessThan: IBreakPointResults<BP>;
  greaterThan: IBreakPointResults<BP>;
  is: IBreakPointResults<BP>;
}

export const browserBreakpoints = {
  mobile: parseInt(breakpoints.mobileMaxPx),
  tablet: parseInt(breakpoints.tabletMaxPx),
  desktop: parseInt(breakpoints.desktopMaxPx),
};

export type BrowserBreakpoints = IBrowser<typeof browserBreakpoints>;

export class BreakpointsPubSub {
  breakpoints: typeof browserBreakpoints;
  subscriptions: Map<string, (browser: BrowserBreakpoints) => void>;
  width: number;
  resizeListener: (this: Window, ev: UIEvent) => void;

  constructor(bp: typeof browserBreakpoints) {
    this.breakpoints = bp;
    this.subscriptions = new Map();
    this.width = 0;
    this.resizeListener = this.onResize.bind(this);
  }

  subscribe(key: string, callback: (browser: BrowserBreakpoints) => void) {
    this.subscriptions.set(key, callback);
    return () => this.unsubscribe(key);
  }

  publish(key: string) {
    this.subscriptions.get(key)?.(this.getBrowser());
  }

  publishAll() {
    const browser = this.getBrowser();
    this.subscriptions.forEach(callback => callback(browser));
  }

  unsubscribe(key: string) {
    this.subscriptions.delete(key);
  }

  unsubscribeAll() {
    this.subscriptions.forEach((_, key) => this.unsubscribe(key));
  }

  onResize() {
    const width = window.innerWidth;
    if (width === this.width) return;
    this.width = width;
    this.publishAll();
  }

  connect() {
    window.addEventListener('resize', this.resizeListener);
  }

  disconnect() {
    this.unsubscribeAll();
    window.removeEventListener('resize', this.resizeListener);
  }

  getBrowser(): BrowserBreakpoints {
    const { innerWidth } = window;
    const { mobile, tablet, desktop } = this.breakpoints;

    const isMobile = innerWidth <= mobile;
    const isTablet = innerWidth > mobile && innerWidth <= tablet;
    const isDesktop = innerWidth > tablet && innerWidth <= desktop;
    const isDesktopLarge = innerWidth > desktop;

    return {
      is: {
        mobile: isMobile,
        tablet: isTablet,
        desktop: isDesktop || isDesktopLarge,
      },
      greaterThan: {
        mobile: isTablet || isDesktop || isDesktopLarge,
        tablet: isDesktop || isDesktopLarge,
        desktop: isDesktopLarge,
      },
      lessThan: {
        mobile: false,
        tablet: isMobile,
        desktop: isTablet || isMobile,
      },
    };
  }
}
